import {
  runCreateFolderWorkflow,
  runCreateResourceFolderWorkflow,
  runDeleteResourceFolderWorkflow,
  runRenameResourceFolderWorkflow,
} from "@/action-dialog";
import { useCreateCanvas } from "@/api/canvas";
import { useSetCanvasFolder } from "@/api/folders";
import { ROUTES } from "@/constants/routes";
import { droppableId } from "@/dnd/ids";
import { SelectableItem } from "@/dnd/SelectableItem";
import { useRegisterWorkspaceDetailDropContext } from "@/dnd/TransferDndProvider";
import { WorkspaceDropTarget } from "@/dnd/WorkspaceDropTarget";
import type { CanvasFolderActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailCanvasFolderTree";
import { CanvasFolderDropTarget } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailCanvasFolderTree";
import { ChatFolderDropTarget } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailChatFolderTree";
import { route } from "@/utils/route";
import {
  FOLDER_RESOURCE_TYPE_CANVAS,
  FOLDER_RESOURCE_TYPE_FLOW_SESSION,
  type Canvas,
  type FileRecord,
  type Folder,
} from "@cohesive/api-client";
import { useDndMonitor } from "@dnd-kit/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { WorkspaceResourceFolderCardItem } from "../components/WorkspaceResourceFolderCardItem";
import {
  HomeBrowserHeader,
  WORKSPACE_BROWSER_TABS,
} from "../home/browser/HomeBrowserHeader";
import { HomeBrowserList } from "../home/browser/HomeBrowserList";
import { NewMenu } from "../home/browser/NewMenu";
import { useBrowserItemRenderers } from "../home/browser/useBrowserItemRenderers";
import { useBrowserSelectionBar } from "../home/browser/useBrowserSelectionBar";
import { useHomeBrowserData } from "../home/hooks/useHomeBrowserData";
import { useHomeBrowserState } from "../home/hooks/useHomeBrowserState";
import { useHomeViewMode } from "../home/hooks/useHomeViewMode";
import { useWorkspaceBrowserData } from "../home/hooks/useWorkspaceBrowserData";
import {
  browserItemId,
  browserItemSelectionId,
  HOME_BROWSER_LIMIT,
  type BrowserItem,
  type HomeTab,
} from "../home/utils/homeBrowserTypes";
import { homeEmptyCopy } from "../home/utils/homeEmptyCopy";
import {
  browserItemMatchesSearch,
  sortBrowserItems,
} from "../home/utils/homeSort";
import { useChatCardActions } from "../hooks/useChatCardActions";
import { useResourceFolderNavigation } from "../hooks/useResourceFolderNavigation";
import { useSelectionKeyboard } from "../hooks/useSelectionKeyboard";
import { useWorkspaceFolderNavigation } from "../hooks/useWorkspaceFolderNavigation";
import { useWorkspaceSelection } from "../hooks/useWorkspaceSelection";
import { getItemName } from "../types";
import { getDirectChildren } from "../utils/folderPaths";
import { buildFolderTrail } from "../utils/folderTrail";
import { handleWorkspaceItemActivate } from "../workspaceItemActivate";
import {
  WorkspaceBrowserBreadcrumbs,
  type WorkspaceBrowserCrumb,
} from "./WorkspaceBrowserBreadcrumbs";
import {
  CanvasBrowserTree,
  ChatBrowserTree,
  FilesBrowserTree,
  type TreeExpansion,
} from "./WorkspaceBrowserTree";

const HOME_CARD_GRID_TRACK_PX = 270;

/** Typed tabs show the workspace's full resource sets with folder handling. */
const isTypedTab = (tab: HomeTab) =>
  tab === "recents" || tab === "canvases" || tab === "chats";

export interface WorkspaceBrowserProps {
  workspaceId: string;
  /** Rendered as the first card/row — upload ghosts on the workspace page. */
  leadingSlot?: React.ReactNode;
  /** File records already represented by an upload ghost; hidden to avoid dupes. */
  suppressedRecordIds?: Set<string>;
  /** Wide-layout toggle state, owned by the surface (it controls the column). */
  wide?: boolean;
  onWideChange?: (wide: boolean) => void;
}

/**
 * The workspace detail page's resource browser. Same tab strip as home with
 * the mixed tab relabeled "Recent" (it keeps home's recents behavior — mixed,
 * folder-free, capped), while Files / Canvases / Chats show ALL workspace
 * resources of that type with full folder handling:
 *
 * - GRID: folder cards first; opening one drills in, tracked per-taxonomy in
 *   URL params (`?path` / `?canvasFolder` / `?chatFolder`) with a breadcrumb
 *   trail whose crumbs double as drop targets.
 * - LIST: folders render as inline-expandable rows (session-local expansion;
 *   an active grid drill-in auto-expands its trail so switching views keeps
 *   context).
 *
 * A non-empty search flattens the typed tab to matching items across all
 * folder levels (no folder rows) — searching a tree reads as noise.
 */
export function WorkspaceBrowser({
  workspaceId,
  leadingSlot,
  suppressedRecordIds,
  wide,
  onWideChange,
}: WorkspaceBrowserProps) {
  const navigate = useNavigate();
  const selection = useWorkspaceSelection();

  const { dispatch, tab, sort } = useHomeBrowserState();
  const [view, setView] = useHomeViewMode(tab, "ws:");
  const [search, setSearch] = useState("");
  const typed = isTypedTab(tab);
  const searching = typed && search.trim().length > 0;

  // ----- folder navigation (grid drill-in), one URL param per taxonomy -----
  const filesNav = useWorkspaceFolderNavigation();
  const canvasNav = useResourceFolderNavigation("canvasFolder");
  const chatNav = useResourceFolderNavigation("chatFolder");

  // Deep links carry a drill-in param but tab state is component-local, so
  // land on the tab the param belongs to instead of the default Recent.
  useEffect(() => {
    if (canvasNav.currentFolderId)
      dispatch({ type: "pickTab", tab: "canvases" });
    else if (chatNav.currentFolderId)
      dispatch({ type: "pickTab", tab: "chats" });
    else if (filesNav.currentPath)
      dispatch({ type: "pickTab", tab: "recents" });
    // Mount-only: later param changes come from in-page navigation on the
    // already-active tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- data -----
  // Recent + Starred reuse home's recents-style data; the typed tabs read the
  // full workspace sets. The underlying react-query keys overlap, so this
  // costs no duplicate fetches.
  const homeData = useHomeBrowserData(tab, workspaceId);
  const wsData = useWorkspaceBrowserData(tab, workspaceId);

  // ----- current-level folder sets (grid cards / tree roots) -----
  const canvasSubfolders = useMemo(
    () =>
      wsData.canvasFolders
        .filter(
          (f) => (f.parent_folder_id ?? null) === canvasNav.currentFolderId,
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [wsData.canvasFolders, canvasNav.currentFolderId],
  );
  const chatSubfolders = useMemo(
    () =>
      wsData.chatFolders
        .filter((f) => (f.parent_folder_id ?? null) === chatNav.currentFolderId)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [wsData.chatFolders, chatNav.currentFolderId],
  );
  const fileSubfolders = useMemo(
    () =>
      getDirectChildren(wsData.files, filesNav.currentPath)
        .filter((r) => r.path.endsWith("/") && r.status !== "deleted")
        .sort((a, b) => getItemName(a).localeCompare(getItemName(b))),
    [wsData.files, filesNav.currentPath],
  );

  // ----- items on the active tab -----
  const items = useMemo<BrowserItem[]>(() => {
    if (!typed) {
      // Recent / Starred: home behavior — mixed, folder-free, capped.
      const visible = homeData.items.filter((item) => {
        if (
          suppressedRecordIds?.size &&
          item.kind === "file" &&
          suppressedRecordIds.has(item.data.id)
        ) {
          return false;
        }
        return browserItemMatchesSearch(item, search);
      });
      return sortBrowserItems(visible, sort).slice(0, HOME_BROWSER_LIMIT);
    }

    let all: BrowserItem[];
    if (tab === "canvases") {
      const canvases = searching
        ? wsData.canvases
        : wsData.canvases.filter(
            (c) => (c.folder_id ?? null) === canvasNav.currentFolderId,
          );
      all = canvases.map((data) => ({ kind: "canvas", data }));
    } else if (tab === "chats") {
      const chats = searching
        ? wsData.chats
        : wsData.chats.filter(
            (c) =>
              (c.flow_session?.folder_id ?? null) === chatNav.currentFolderId,
          );
      all = chats.map((data) => ({ kind: "chat", data }));
    } else {
      const files = searching
        ? wsData.files
        : getDirectChildren(wsData.files, filesNav.currentPath);
      all = files
        .filter(
          (r) =>
            !r.path.endsWith("/") &&
            r.status !== "pending" &&
            r.status !== "deleted" &&
            !suppressedRecordIds?.has(r.id),
        )
        .map((data) => ({ kind: "file", data }));
    }
    return sortBrowserItems(
      all.filter((item) => browserItemMatchesSearch(item, search)),
      sort,
    );
  }, [
    typed,
    tab,
    searching,
    homeData.items,
    wsData.canvases,
    wsData.chats,
    wsData.files,
    canvasNav.currentFolderId,
    chatNav.currentFolderId,
    filesNav.currentPath,
    suppressedRecordIds,
    search,
    sort,
  ]);

  // Folder open (files): clear stale selection, then jump to the folder's
  // full path. Not `pushSegment` — the list tree can open folders nested
  // below the current level, where appending one segment would mis-navigate.
  const clearSelection = selection.clear;
  const handleFolderOpen = useCallback(
    (record: FileRecord) => {
      if (!record.path.endsWith("/")) return;
      clearSelection();
      filesNav.navigateToPath(record.path);
    },
    [clearSelection, filesNav],
  );

  // ----- renderers -----
  const canvasesInScope = useMemo(
    () =>
      typed
        ? wsData.canvases
        : homeData.items.flatMap((i): Canvas[] =>
            i.kind === "canvas" ? [i.data] : [],
          ),
    [typed, wsData.canvases, homeData.items],
  );

  const {
    renderGridItem,
    renderRow,
    renderFolderCard,
    canvasActions,
    fileActions,
    chatActions,
  } = useBrowserItemRenderers({
    workspaceId,
    canvasesInScope,
    filesInScope: wsData.files,
    draggable: true,
    onFolderOpen: handleFolderOpen,
  });

  // ----- chat/canvas creation (files new items into the current folder) -----
  const chatCardActions = useChatCardActions({ workspaceId });
  const createCanvas = useCreateCanvas({
    onError: () => toast.error("Failed to create canvas"),
  });
  const setCanvasFolder = useSetCanvasFolder();
  // Creating while inside a folder view files the new canvas there. The
  // canvas exists either way, so a filing failure only means it landed at the
  // workspace root — say so instead of failing silently.
  const handleCreateCanvas = useCallback(
    (folderId: string | null = canvasNav.currentFolderId) => {
      if (createCanvas.isPending) return;
      createCanvas.mutate(
        { workspaceId },
        {
          onSuccess: (newCanvas) => {
            if (folderId) {
              setCanvasFolder.mutate(
                { canvasId: newCanvas.id, folderId, workspaceId },
                {
                  onError: () =>
                    toast.error(
                      "Canvas created at the workspace root — couldn't file it into the folder.",
                    ),
                },
              );
            }
            navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: newCanvas.id }));
          },
        },
      );
    },
    [
      workspaceId,
      canvasNav.currentFolderId,
      createCanvas,
      setCanvasFolder,
      navigate,
    ],
  );

  // ----- folder CRUD (kebab / context menu), per taxonomy -----
  // Archiving the folder you're inside moves the view up to its parent.
  const canvasFolderActions = useMemo<CanvasFolderActions>(
    () => ({
      onOpen: (folder) => canvasNav.navigate(folder.id),
      onNewResource: (folderId) => handleCreateCanvas(folderId),
      onNewSubfolder: (parentFolderId) => {
        void runCreateResourceFolderWorkflow({
          workspaceId,
          resourceType: FOLDER_RESOURCE_TYPE_CANVAS,
          parentFolderId,
        });
      },
      onRename: (folder) => {
        void runRenameResourceFolderWorkflow({ workspaceId, folder });
      },
      onDelete: (folder) => {
        void runDeleteResourceFolderWorkflow({ workspaceId, folder }).then(
          (deleted) => {
            if (deleted && folder.id === canvasNav.currentFolderId) {
              canvasNav.navigate(folder.parent_folder_id ?? null);
            }
          },
        );
      },
    }),
    [workspaceId, handleCreateCanvas, canvasNav],
  );
  const chatFolderActions = useMemo<CanvasFolderActions>(
    () => ({
      onOpen: (folder) => chatNav.navigate(folder.id),
      onNewResource: (folderId) => chatCardActions.onCreate(folderId),
      onNewSubfolder: (parentFolderId) => {
        void runCreateResourceFolderWorkflow({
          workspaceId,
          resourceType: FOLDER_RESOURCE_TYPE_FLOW_SESSION,
          parentFolderId,
        });
      },
      onRename: (folder) => {
        void runRenameResourceFolderWorkflow({ workspaceId, folder });
      },
      onDelete: (folder) => {
        void runDeleteResourceFolderWorkflow({ workspaceId, folder }).then(
          (deleted) => {
            if (deleted && folder.id === chatNav.currentFolderId) {
              chatNav.navigate(folder.parent_folder_id ?? null);
            }
          },
        );
      },
    }),
    [workspaceId, chatCardActions, chatNav],
  );

  const canvasFolderChildCount = useCallback(
    (folder: Folder) =>
      wsData.canvasFolders.filter((f) => f.parent_folder_id === folder.id)
        .length +
      wsData.canvases.filter((c) => c.folder_id === folder.id).length,
    [wsData.canvasFolders, wsData.canvases],
  );
  const chatFolderChildCount = useCallback(
    (folder: Folder) =>
      wsData.chatFolders.filter((f) => f.parent_folder_id === folder.id)
        .length +
      wsData.chats.filter(
        (c) => (c.flow_session?.folder_id ?? null) === folder.id,
      ).length,
    [wsData.chatFolders, wsData.chats],
  );
  const fileFolderChildCount = useCallback(
    (record: FileRecord) =>
      getDirectChildren(wsData.files, record.path).filter(
        (r) => r.status !== "pending" && r.status !== "deleted",
      ).length,
    [wsData.files],
  );

  // ----- grid folder cards at the current level -----
  const folderCards = useMemo<React.ReactNode[] | undefined>(() => {
    if (!typed || searching || view !== "grid") return undefined;
    if (tab === "recents") {
      return fileSubfolders.map((record) => renderFolderCard(record, 0));
    }
    const resource = tab === "canvases";
    const subfolders = resource ? canvasSubfolders : chatSubfolders;
    const actions = resource ? canvasFolderActions : chatFolderActions;
    const childCount = resource ? canvasFolderChildCount : chatFolderChildCount;
    const DropTarget = resource ? CanvasFolderDropTarget : ChatFolderDropTarget;
    const dropIdFor = resource
      ? droppableId.detailCanvasFolder
      : droppableId.detailChatFolder;
    const selPrefix = resource ? "canvas-folder" : "chat-folder";

    return subfolders.map((folder) => {
      const selId = `${selPrefix}:${folder.id}`;
      return (
        <DropTarget
          key={folder.id}
          workspaceId={workspaceId}
          folderId={folder.id}
          dropId={dropIdFor(workspaceId, folder.id)}
        >
          <SelectableItem
            id={selId}
            kind="folder"
            draggable={false}
            selectedIds={selection.selectedIds}
            selectOnly={selection.selectOnly}
          >
            <WorkspaceResourceFolderCardItem
              folder={folder}
              childCount={childCount(folder)}
              isSelected={selection.isSelected(selId)}
              onClick={(e) =>
                handleWorkspaceItemActivate({
                  isSelected: selection.isSelected(selId),
                  e,
                  onOpen: () => actions.onOpen(folder),
                  onSelect: () => selection.cardClick(selId, e),
                })
              }
              onOpen={() => actions.onOpen(folder)}
              onNewResource={() => actions.onNewResource(folder.id)}
              onNewSubfolder={() => actions.onNewSubfolder(folder.id)}
              onRename={() => actions.onRename(folder)}
              onArchive={() => actions.onDelete(folder)}
            />
          </SelectableItem>
        </DropTarget>
      );
    });
  }, [
    typed,
    searching,
    view,
    tab,
    fileSubfolders,
    renderFolderCard,
    canvasSubfolders,
    chatSubfolders,
    canvasFolderActions,
    chatFolderActions,
    canvasFolderChildCount,
    chatFolderChildCount,
    workspaceId,
    selection,
  ]);

  // ----- list-view inline folder tree -----
  // Session-local expansion per tab; keys are folder ids (canvas/chat) or
  // folder paths (files).
  const [expandedByTab, setExpandedByTab] = useState<
    Record<string, Set<string>>
  >({});
  const expansion = useMemo<TreeExpansion>(() => {
    const current = expandedByTab[tab] ?? new Set<string>();
    return {
      isOpen: (key) => current.has(key),
      onToggle: (key) =>
        setExpandedByTab((prev) => {
          const next = new Set(prev[tab] ?? []);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return { ...prev, [tab]: next };
        }),
    };
  }, [expandedByTab, tab]);

  const sortItems = useCallback(
    (arr: BrowserItem[]) => sortBrowserItems(arr, sort),
    [sort],
  );
  const renderTreeRow = useCallback(
    (item: BrowserItem, depth: number) =>
      depth > 0 ? (
        <div key={browserItemId(item)} style={{ paddingLeft: depth * 20 }}>
          {renderRow(item)}
        </div>
      ) : (
        renderRow(item)
      ),
    [renderRow],
  );

  // The tree roots at the current drill-in level — same scope the grid shows,
  // so a double-click drill-in (or a crumb) behaves identically across views;
  // single-click expansion then explores beneath that level inline.
  const listBody = useMemo<React.ReactNode>(() => {
    if (!typed || searching || view !== "list") return undefined;
    // An empty tree yields no rows, and its presence suppresses the shared
    // empty state — fall back to the default (empty) body instead. A level
    // with neither folders nor loose items has no subtree content either.
    const levelFolderCount =
      tab === "canvases"
        ? canvasSubfolders.length
        : tab === "chats"
          ? chatSubfolders.length
          : fileSubfolders.length;
    if (levelFolderCount === 0 && items.length === 0) return undefined;

    if (tab === "canvases") {
      return (
        <CanvasBrowserTree
          canvases={wsData.canvases}
          parentFolderId={canvasNav.currentFolderId}
          workspaceId={workspaceId}
          folders={wsData.canvasFolders}
          expansion={expansion}
          sortItems={sortItems}
          renderRow={renderTreeRow}
          folderDeps={(folder) => ({
            onOpen: () => canvasFolderActions.onOpen(folder),
            onNewResource: () => canvasFolderActions.onNewResource(folder.id),
            onNewSubfolder: () => canvasFolderActions.onNewSubfolder(folder.id),
            onRename: () => canvasFolderActions.onRename(folder),
            onArchive: () => canvasFolderActions.onDelete(folder),
          })}
          folderChildCount={canvasFolderChildCount}
        />
      );
    }
    if (tab === "chats") {
      return (
        <ChatBrowserTree
          chats={wsData.chats}
          parentFolderId={chatNav.currentFolderId}
          workspaceId={workspaceId}
          folders={wsData.chatFolders}
          expansion={expansion}
          sortItems={sortItems}
          renderRow={renderTreeRow}
          folderDeps={(folder) => ({
            onOpen: () => chatFolderActions.onOpen(folder),
            onNewResource: () => chatFolderActions.onNewResource(folder.id),
            onNewSubfolder: () => chatFolderActions.onNewSubfolder(folder.id),
            onRename: () => chatFolderActions.onRename(folder),
            onArchive: () => chatFolderActions.onDelete(folder),
          })}
          folderChildCount={chatFolderChildCount}
        />
      );
    }
    return (
      <FilesBrowserTree
        records={wsData.files}
        parentPath={filesNav.currentPath}
        workspaceId={workspaceId}
        expansion={expansion}
        suppressedRecordIds={suppressedRecordIds}
        sortItems={sortItems}
        renderRow={renderTreeRow}
        fileFolderDeps={{
          onOpen: handleFolderOpen,
          onRename: fileActions.rename,
          onDelete: fileActions.delete,
        }}
        folderChildCount={fileFolderChildCount}
      />
    );
  }, [
    typed,
    searching,
    view,
    tab,
    workspaceId,
    items.length,
    canvasSubfolders.length,
    chatSubfolders.length,
    fileSubfolders.length,
    wsData.canvases,
    wsData.canvasFolders,
    wsData.chats,
    wsData.chatFolders,
    wsData.files,
    canvasNav.currentFolderId,
    chatNav.currentFolderId,
    filesNav.currentPath,
    expansion,
    sortItems,
    renderTreeRow,
    canvasFolderActions,
    chatFolderActions,
    canvasFolderChildCount,
    chatFolderChildCount,
    fileFolderChildCount,
    handleFolderOpen,
    fileActions,
    suppressedRecordIds,
  ]);

  // ----- breadcrumbs (grid drill-in) -----
  const crumbs = useMemo<WorkspaceBrowserCrumb[]>(() => {
    if (!typed || searching) return [];
    if (tab === "recents") {
      if (filesNav.segments.length === 0) return [];
      const wrapInDropTarget =
        (parentPath: string) => (children: React.ReactNode) => (
          <WorkspaceDropTarget
            workspaceId={workspaceId}
            parentPath={parentPath}
            className="rounded-sm"
          >
            {children}
          </WorkspaceDropTarget>
        );
      return [
        {
          key: "__root__",
          label: "Files",
          onNavigate: () => filesNav.navigateToPath(""),
          wrap: wrapInDropTarget(""),
        },
        ...filesNav.segments.map((seg, i) => {
          const segmentPath = filesNav.segments.slice(0, i + 1).join("/") + "/";
          const isLast = i === filesNav.segments.length - 1;
          return {
            key: segmentPath,
            label: seg,
            onNavigate: isLast
              ? undefined
              : () => filesNav.navigateToPath(segmentPath),
            wrap: isLast ? undefined : wrapInDropTarget(segmentPath),
          };
        }),
      ];
    }

    const resource = tab === "canvases";
    const nav = resource ? canvasNav : chatNav;
    if (!nav.currentFolderId) return [];
    const trail = buildFolderTrail(
      resource ? wsData.canvasFolders : wsData.chatFolders,
      nav.currentFolderId,
    );
    const DropTarget = resource ? CanvasFolderDropTarget : ChatFolderDropTarget;
    const dropIdFor = resource
      ? droppableId.detailCanvasFolder
      : droppableId.detailChatFolder;
    const wrapInFolderDrop =
      (folderId: string | null) => (children: React.ReactNode) => (
        <DropTarget
          workspaceId={workspaceId}
          folderId={folderId}
          dropId={`${dropIdFor(workspaceId, folderId)}:crumb`}
          className="rounded-sm"
        >
          {children}
        </DropTarget>
      );
    return [
      {
        key: "__root__",
        label: resource ? "Canvases" : "Chats",
        onNavigate: () => nav.navigate(null),
        wrap: wrapInFolderDrop(null),
      },
      ...trail.map((f, i) => {
        const isLast = i === trail.length - 1;
        return {
          key: f.id,
          label: f.name,
          onNavigate: isLast ? undefined : () => nav.navigate(f.id),
          wrap: isLast ? undefined : wrapInFolderDrop(f.id),
        };
      }),
    ];
  }, [
    typed,
    searching,
    tab,
    filesNav,
    canvasNav,
    chatNav,
    wsData.canvasFolders,
    wsData.chatFolders,
    workspaceId,
  ]);

  // ----- selection plumbing -----
  const orderedSelectionIds = useMemo(
    () =>
      items
        .map(browserItemSelectionId)
        .filter((id): id is string => id !== null),
    [items],
  );
  useEffect(() => {
    selection.setOrderedIds(orderedSelectionIds);
  }, [orderedSelectionIds, selection]);

  const selectionResolutionItems = useMemo<BrowserItem[]>(
    () => [
      ...homeData.items,
      ...wsData.canvases.map((data): BrowserItem => ({ kind: "canvas", data })),
      ...wsData.files.map((data): BrowserItem => ({ kind: "file", data })),
      ...wsData.chats.map((data): BrowserItem => ({ kind: "chat", data })),
    ],
    [homeData.items, wsData.canvases, wsData.files, wsData.chats],
  );
  const { selectionBar, openSelectedId } = useBrowserSelectionBar({
    items: selectionResolutionItems,
    workspaceId,
    allRecords: wsData.files,
    canvasActions,
    fileActions,
    openChat: chatActions.open,
    onFolderOpen: handleFolderOpen,
  });
  useSelectionKeyboard(openSelectedId);

  // ----- drag dispatch: register this page as a workspace-detail source -----
  const canvasMap = useMemo(() => {
    const m = new Map<string, Canvas>();
    for (const c of wsData.canvases) m.set(c.id, c);
    return m;
  }, [wsData.canvases]);
  const fileRecordMap = useMemo(() => {
    const m = new Map<string, FileRecord>();
    for (const r of wsData.files) m.set(r.id, r);
    return m;
  }, [wsData.files]);
  const inboxItemMap = useMemo(() => {
    const m = new Map<string, (typeof wsData.chats)[number]>();
    for (const item of wsData.chats) m.set(item.id, item);
    return m;
  }, [wsData.chats]);
  useRegisterWorkspaceDetailDropContext(
    useMemo(
      () => ({
        sourceWorkspaceId: workspaceId,
        canvasMap,
        fileRecordMap,
        inboxItemMap,
      }),
      [workspaceId, canvasMap, fileRecordMap, inboxItemMap],
    ),
  );
  // A drag consumed the selection (it travels with the payload) — clear it.
  useDndMonitor({ onDragEnd: () => selection.clear() });

  // ----- "+ New" -----
  const onNewFolder = useMemo(() => {
    if (!typed) return undefined;
    if (tab === "recents") {
      return () =>
        void runCreateFolderWorkflow({
          workspaceId,
          currentPath: filesNav.currentPath,
        });
    }
    if (tab === "canvases") {
      return () =>
        void runCreateResourceFolderWorkflow({
          workspaceId,
          resourceType: FOLDER_RESOURCE_TYPE_CANVAS,
          parentFolderId: canvasNav.currentFolderId,
        });
    }
    return () =>
      void runCreateResourceFolderWorkflow({
        workspaceId,
        resourceType: FOLDER_RESOURCE_TYPE_FLOW_SESSION,
        parentFolderId: chatNav.currentFolderId,
      });
  }, [
    typed,
    tab,
    workspaceId,
    filesNav.currentPath,
    canvasNav.currentFolderId,
    chatNav.currentFolderId,
  ]);

  const activeData = typed ? wsData : homeData;

  // Inside a drilled-into folder the tab-level empty copy ("No canvases
  // yet…") would be wrong — the workspace has content, this folder doesn't.
  // Mirror the rail's folder empty state instead; "+ New" and drag-drop
  // still target the folder being viewed.
  const inFolder =
    (tab === "canvases" && !!canvasNav.currentFolderId) ||
    (tab === "chats" && !!chatNav.currentFolderId) ||
    (tab === "recents" && !!filesNav.currentPath);
  const emptyCopy =
    typed && inFolder
      ? {
          title:
            tab === "canvases"
              ? "No canvases in folder."
              : tab === "chats"
                ? "No chats in folder."
                : "No files in folder.",
          hint: "Create something with + New or drag items in",
        }
      : homeEmptyCopy(tab, true);

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-medium text-n-90">Workspace Activity</h2>
        {selectionBar}
      </div>
      <HomeBrowserHeader
        tabs={WORKSPACE_BROWSER_TABS}
        tab={tab}
        onTabChange={(next) => dispatch({ type: "pickTab", tab: next })}
        view={view}
        onViewChange={setView}
        sort={sort}
        onSortChange={(next) => dispatch({ type: "setSort", sort: next })}
        search={search}
        onSearchChange={setSearch}
        searchLabel="Search this workspace"
        wide={wide}
        onWideChange={onWideChange}
        newMenu={
          <NewMenu
            onNewCanvas={() =>
              handleCreateCanvas(
                tab === "canvases" ? canvasNav.currentFolderId : null,
              )
            }
            onNewChat={() =>
              chatCardActions.onCreate(
                tab === "chats"
                  ? (chatNav.currentFolderId ?? undefined)
                  : undefined,
              )
            }
            onNewFolder={onNewFolder}
            isCreating={createCanvas.isPending || chatCardActions.isCreating}
          />
        }
      />
      {crumbs.length > 1 && <WorkspaceBrowserBreadcrumbs crumbs={crumbs} />}
      <HomeBrowserList
        view={view}
        items={items}
        isPending={activeData.isPending}
        isError={activeData.isError}
        onRetry={activeData.refetch}
        empty={emptyCopy}
        renderGridItem={renderGridItem}
        renderRow={renderRow}
        gridTrackPx={HOME_CARD_GRID_TRACK_PX}
        sort={sort}
        onSortFieldChange={(field) => dispatch({ type: "sortByField", field })}
        folderCards={folderCards}
        listBody={listBody}
        leadingSlot={leadingSlot}
      />
    </section>
  );
}
